package com.algorix.web

import androidx.compose.runtime.Composable
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private const val SELLERSCOPE = "https://sellerscopeai.app"

/** The two pages of the site; Cloudflare Pages serves index.html for /e-commerce as well. */
enum class Page {
    HOME, COMMERCE;

    /** Link to a section of the home page that works from either page. */
    fun home(anchor: String): String = if (this == HOME) anchor else "/$anchor"

    companion object {
        const val COMMERCE_PATH = "/e-commerce"

        fun current(): Page = if (window.location.pathname.trimEnd('/') == COMMERCE_PATH) COMMERCE else HOME
    }
}

private class Group(val id: String, val key: String)

private val GROUPS = listOf(
    Group("g1", "Three.js"),
    Group("g2", "Etsy Open API v3"),
    Group("g3", "OpenAI"),
    Group("g4", "ShipEntegra"),
)

@Composable
fun CommercePage(depth: Depth) {
    CommerceHero()
    Features()
    Platform(depth)
    Closing()
}

@Composable
private fun CommerceHero() {
    Section({
        classes("section", "ec-hero")
        id("top")
        aria("labelledby", "ec-title")
        attr("data-zone", "")
        attr("data-nav", "ecommerce")
    }) {
        Div({ classes("wrap", "ec-grid") }) {
            Div {
                P({ classes("eyebrow") }) { Tx("ec.eyebrow") }
                H1({ id("ec-title") }) {
                    Span { Tx("ec.l1") }
                    Span({ classes("mute") }) { Tx("ec.l2") }
                }
                P({ classes("lede") }) { Tx("ec.lede") }
                Div({ classes("ctas") }) {
                    ExternalLink(SELLERSCOPE, { classes("btn", "btn-solid") }) {
                        Span { Tx("ec.cta1") }
                        Glyph("0 0 16 16", "M5 11 11 5M6 5h5v5")
                    }
                    A(href = "#features", attrs = { classes("btn", "btn-glass") }) { Tx("ec.cta2") }
                }
            }
            Div({ classes("ec-stage") }) {
                Span({ classes("ec-glow"); aria("hidden", "true") })
                Figure({ classes("ec-frame") }) {
                    Img(src = "/assets/sellerscope-dashboard.jpg", alt = t("ec.shot.alt")) {
                        attr("width", "1600"); attr("height", "618"); attr("decoding", "async")
                    }
                }
            }
        }
    }
}

@Composable
private fun Features() {
    Section({
        classes("section", "ec-feats")
        id("features")
        aria("labelledby", "ec-feat-title")
        attr("data-zone", "scan")
        attr("data-nav", "ecommerce")
    }) {
        Div({ classes("wrap") }) {
            Div({ classes("w-head", "ec-head") }) {
                Img(src = "/assets/sellerscope.png", alt = t("ec.icon")) {
                    classes("app-icon")
                    attr("width", "192"); attr("height", "192"); attr("loading", "lazy"); attr("decoding", "async")
                }
                Div {
                    H2({ classes("h2"); id("ec-feat-title") }) { Tx("ec.feat.title") }
                }
            }
            P({ classes("sec-lede") }) { Tx("ec.feat.lede") }
            Div({ classes("ec-cards") }) {
                for (g in GROUPS) {
                    Div({ classes("ec-card") }) {
                        Span({ classes("ap-key") }) { Text(g.key) }
                        H3 { Tx("${g.id}.t") }
                        Ul({ classes("feats") }) {
                            for (f in 1..3) Li {
                                H4 { Tx("${g.id}.f${f}t") }
                                P { Tx("${g.id}.f${f}d") }
                            }
                        }
                    }
                }
            }
            Ul({ classes("chips", "ec-chips") }) {
                for (m in 1..3) Li { Tx("ec.m$m") }
            }
        }
    }
}

@Composable
private fun Platform(depth: Depth) {
    Section({
        classes("section", "approach")
        id("platform")
        aria("labelledby", "platform-title")
        attr("data-zone", "vault")
        attr("data-nav", "ecommerce")
    }) {
        Div({ classes("wrap", "ap-grid") }) {
            Div({ classes("ap-copy") }) {
                P({ classes("eyebrow") }) { Tx("st.eyebrow") }
                H2({ classes("h2"); id("platform-title") }) { Tx("st.title") }
                P({ classes("sec-lede"); style { property("margin-top", "22px") } }) { Tx("st.lede") }
                Ul({ classes("ap-points") }) {
                    Point("Kobweb · Compose HTML", "sp1")
                    Point("Three.js", "sp2")
                    Point("Firebase · Scheduler", "sp3")
                }
            }
            StackFigure(depth)
        }
    }
}

@Composable
private fun StackFigure(depth: Depth) {
    Figure({
        classes("kmp")
        if (depth.kmp3d) classes("is-3d")
        id("kmp")
        aria("labelledby", "stack-cap")
        depth.kmpAsm?.let { style { property("--asm", it.fx(4)) } }
        ref { depth.kmp = it; onDispose { depth.kmp = null } }
    }) {
        Div({ classes("kmp-view") }) {
            Div({ classes("board") }) {
                P({ classes("tier", "t1") }) { Tx("sf.t1") }
                Div({ classes("node", "n-shared") }) {
                    P({ classes("nk") }) { Text("Kotlin") }
                    P({ classes("nt") }) { Tx("sf.shared") }
                    P({ classes("ns") }) { Text("Compose HTML · Silk · Ktor") }
                }
                Span({ classes("fork"); aria("hidden", "true") }) {
                    Span({ classes("fork-lbl") }) { Text("Kobweb") }
                }
                P({ classes("tier", "t2") }) { Tx("sf.t2") }
                Node("r2", "la", "jsMain", "sf.web", "Kobweb · Compose HTML")
                Node("r2", "lb", "three.js", "sf.3d", "WebGL · GLB")
                P({ classes("tier", "t3") }) { Tx("sf.t3") }
                Node("r3", "la", "jvmMain", "sf.api", null)
                Node("r3", "lb", "Scheduler", "sf.jobs", null)
                P({ classes("tier", "t4") }) { Tx("sf.t4") }
                Div({ classes("node", "r4", "la", "store-n") }) {
                    P({ classes("nk") }) { Tx("sf.data") }
                    P({ classes("nt") }) { Text("Firebase") }
                }
                Div({ classes("node", "r4", "lb", "store-n") }) {
                    P({ classes("nk") }) { Tx("sf.integrations") }
                    P({ classes("nt") }) { Text("Etsy · ShipEntegra · OpenAI") }
                }
            }
        }
        Figcaption({ id("stack-cap") }) { Tx("sf.cap") }
    }
}

@Composable
private fun Closing() {
    Section({
        classes("section", "ec-cta")
        aria("labelledby", "ec-cta-title")
        attr("data-zone", "")
        attr("data-nav", "ecommerce")
    }) {
        Div({ classes("wrap") }) {
            Div({ classes("ec-cta-card") }) {
                Img(src = "/assets/sellerscope.png", alt = "") {
                    classes("app-icon")
                    attr("width", "192"); attr("height", "192"); attr("loading", "lazy"); attr("decoding", "async")
                }
                Div({ classes("ec-cta-text") }) {
                    H2({ id("ec-cta-title") }) { Text("SellerScope") }
                    P { Tx("cta.d") }
                }
                ExternalLink(SELLERSCOPE, { classes("store", "is-live") }) {
                    Span({ classes("s-txt") }) {
                        Span({ classes("s-sub") }) { Tx("cta.sub") }
                        Span({ classes("s-name") }) { Text("sellerscopeai.app") }
                    }
                }
            }
            P({ classes("ec-note") }) { Tx("cta.etsy") }
        }
    }
}
